import { useEffect, useRef } from 'react';

import clsx from 'clsx';

import {
  PPT_MAKER_THEMES,
  SlideType,
  TextAlignment,
} from '@lessonPlanner/constants';

import { Button } from '../Button';

import { LayoutStyles, PptSlide } from '../../types/ppt-slide';

import { PptContent } from './PptContent';

interface PptSlidePreviewPanelsProps {
  slides: PptSlide[];
  currentSlide: number;
  setCurrentSlide: (index: number) => void;
  selectedFont: { name: string };
  selectedTheme: { id: string };
  getSlideStyles: (
    theme: { id: string },
    slideType: string,
    index: number,
  ) => Record<string, unknown>;
}

export function PptSlidePreviewPanels({
  slides,
  currentSlide,
  setCurrentSlide,
  selectedFont,
  selectedTheme,
  getSlideStyles,
}: PptSlidePreviewPanelsProps) {
  const previewTextStyles = {
    color: PPT_MAKER_THEMES[selectedTheme.id].text,
    fontFamily: selectedFont.name,
  };

  const renderSlideNumber = (index: number) => (
    <div className="absolute right-1 top-1 z-10 rounded-md bg-black/30 px-1 py-0.5 text-[10px] font-medium text-white opacity-80 sm:px-1.5 sm:text-xs">
      {index + 1}
    </div>
  );

  const renderTitleSlide = (slide) => (
    <div className="flex h-full w-full items-center justify-center">
      <span
        className="line-clamp-2 max-w-full break-words px-1 text-center text-[clamp(4px,1.2vw,10px)] font-bold"
        style={{
          ...previewTextStyles,
          color: PPT_MAKER_THEMES[selectedTheme.id].text,
        }}
      >
        {slide.title}
      </span>
    </div>
  );

  // Special render for quadrant layout
  const renderQuadrantPreview = (slide) => {
    if (!Array.isArray(slide.content) || slide.content.length === 0)
      return null;

    const items = slide.content.slice(0, 4);
    const textColor = PPT_MAKER_THEMES[selectedTheme.id].text;

    return (
      <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-1 p-[1px]">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="flex flex-col items-center justify-center overflow-hidden p-1 text-center"
          >
            <div className="w-full overflow-hidden text-center">
              <PptContent
                isPreview
                className="text-[clamp(2.5px,0.6vw,6px)]"
                fontFamily={selectedFont.name}
                textColor={textColor}
                content={
                  typeof item === 'object' && item.content ? item.content : ''
                }
                typography={{
                  fontSize: 'min(4px, 0.5cqw, 0.5cqh)',
                  textAlign: TextAlignment.LEFT,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Special render for comparison layout
  const renderComparisonPreview = (slide) => {
    if (!Array.isArray(slide.content) || slide.content.length === 0)
      return null;

    const columns = slide.content.slice(0, 2);
    const textColor = PPT_MAKER_THEMES[selectedTheme.id].text;

    return (
      <div className="flex h-full w-full">
        {columns.map((column, idx) => (
          <div
            key={idx}
            className="flex h-full flex-1 flex-col p-1 text-center"
          >
            {column.title && (
              <div
                className="mb-1 overflow-hidden text-[clamp(3px,0.6cqw,0.6cqh)] font-semibold"
                style={{ color: textColor }}
              >
                {column.title}
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              <PptContent
                isPreview
                className="text-[clamp(2.5px,0.5vw,5px)]"
                content={column.content}
                fontFamily={selectedFont.name}
                textColor={textColor}
                typography={{
                  fontSize: 'min(4px, 0.5cqw, 0.5cqh)',
                  textAlign: TextAlignment.LEFT,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Special render for horizontal split layout
  const renderHorizontalSplitPreview = (slide) => {
    if (!Array.isArray(slide.content) || slide.content.length === 0)
      return null;

    const columns = slide.content.slice(0, 2);
    const textColor = PPT_MAKER_THEMES[selectedTheme.id].text;

    return (
      <div className="flex h-full w-full">
        {columns.map((column, idx) => (
          <div
            key={idx}
            className="flex h-full flex-1 flex-col p-1 text-center"
          >
            {column.title && (
              <div
                className="mb-1 overflow-hidden text-[clamp(3px,0.6cqw,0.6cqh)] font-semibold"
                style={{ color: textColor }}
              >
                {column.title}
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              <PptContent
                isPreview
                className="text-[clamp(2.5px,0.5vw,5px)]"
                content={column.content}
                fontFamily={selectedFont.name}
                textColor={textColor}
                typography={{
                  fontSize: 'min(4px, 0.5cqw, 0.5cqh)',
                  textAlign: TextAlignment.LEFT,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Special render for vertical split layout
  const renderVerticalSplitPreview = (slide) => {
    if (!Array.isArray(slide.content) || slide.content.length === 0)
      return null;

    const columns = slide.content.slice(0, 2);
    const textColor = PPT_MAKER_THEMES[selectedTheme.id].text;

    return (
      <div className="flex h-full w-full flex-col">
        {columns.map((column, idx) => (
          <div key={idx} className="flex flex-1 flex-col p-1 text-center">
            {column.title && (
              <div
                className="overflow-hidden text-[clamp(3px,0.6cqw,0.6cqh)] font-semibold"
                style={{ color: textColor }}
              >
                {column.title}
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              <PptContent
                isPreview
                className="text-[clamp(2.5px,0.5vw,5px)]"
                content={column.content}
                fontFamily={selectedFont.name}
                textColor={textColor}
                typography={{
                  fontSize: 'min(4px, 0.5cqw, 0.5cqh)',
                  textAlign: TextAlignment.LEFT,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCenteredFocusPreview = (slide) => {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <PptContent
          isPreview
          className="text-[clamp(3px,0.6vw,6px)] font-bold"
          content={slide.content}
          fontFamily={selectedFont.name}
          textColor={PPT_MAKER_THEMES[selectedTheme.id].text}
        />
      </div>
    );
  };

  const renderLeftContentPreview = (slide) => {
    return (
      <div className="flex h-full w-full items-center justify-start px-0.5">
        <div className="ml-1 max-w-[45%] whitespace-pre-wrap text-center">
          <PptContent
            isPreview
            className="text-center text-[clamp(2.5px,0.5vw,5px)]"
            content={slide.content}
            fontFamily={selectedFont.name}
            textColor={PPT_MAKER_THEMES[selectedTheme.id].text}
            typography={{
              fontSize: 'min(4px, 0.5cqw, 0.5cqh)',
              textAlign: TextAlignment.CENTER,
            }}
          />
        </div>
      </div>
    );
  };

  const renderRightContentPreview = (slide) => {
    return (
      <div className="flex h-full w-full items-center justify-end px-0.5">
        <div className="mr-1 max-w-[45%] whitespace-pre-wrap text-center">
          <PptContent
            isPreview
            className="text-center text-[clamp(2.5px,0.5vw,5px)]"
            content={slide.content}
            fontFamily={selectedFont.name}
            textColor={PPT_MAKER_THEMES[selectedTheme.id].text}
            typography={{
              fontSize: 'min(4px, 0.5cqw, 0.5cqh)',
              textAlign: TextAlignment.CENTER,
            }}
          />
        </div>
      </div>
    );
  };

  const renderContentSlide = (slide) => {
    return (
      <div className="h-full pt-0">
        <span
          className="line-clamp-1 block w-full break-words pb-0.5 text-left text-[clamp(3px,0.8vw,7px)] font-bold"
          style={{
            ...previewTextStyles,
            color: PPT_MAKER_THEMES[selectedTheme.id].text,
          }}
        >
          {slide.title}
        </span>
        <div className="mt-0.5 h-[calc(100%-1rem)] w-full p-1">
          {slide.layout === LayoutStyles.QUADRANT ? (
            renderQuadrantPreview(slide)
          ) : slide.layout === LayoutStyles.COMPARISON ? (
            renderComparisonPreview(slide)
          ) : slide.layout === LayoutStyles.HORIZONTAL_SPLIT ? (
            renderHorizontalSplitPreview(slide)
          ) : slide.layout === LayoutStyles.VERTICAL_SPLIT ? (
            renderVerticalSplitPreview(slide)
          ) : slide.layout === LayoutStyles.CENTERED_FOCUS ? (
            renderCenteredFocusPreview(slide)
          ) : slide.layout === LayoutStyles.LEFT_CONTENT ? (
            renderLeftContentPreview(slide)
          ) : slide.layout === LayoutStyles.RIGHT_CONTENT ? (
            renderRightContentPreview(slide)
          ) : (
            //for default layout
            <div
              className="mt-0.5 text-left text-[clamp(2.5px,0.5vw,5px)]"
              style={{ color: PPT_MAKER_THEMES[selectedTheme.id].text }}
            >
              <PptContent
                isPreview
                content={slide.content}
                fontFamily={selectedFont.name}
                textColor={PPT_MAKER_THEMES[selectedTheme.id].text}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const slideRefs = useRef<(HTMLDivElement | null)[]>(
    new Array(slides.length).fill(null),
  );
  useEffect(() => {
    const animationId = requestAnimationFrame(() => {
      const node = slideRefs?.current?.[currentSlide];
      if (node && currentSlide >= 0 && currentSlide < slides.length) {
        node.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    });

    return () => cancelAnimationFrame(animationId);
  }, [currentSlide, slides.length]);

  const renderSlidePreview = (slide, index) => {
    const isContentSlide = Boolean(slide.content);
    const slideStyles = getSlideStyles(
      selectedTheme,
      isContentSlide ? SlideType.CONTENT : SlideType.TITLE,
      index,
    );
    const isCurrentSlide = currentSlide === index;

    return (
      <div
        key={index}
        ref={(el) => {
          slideRefs.current[index] = el;
        }}
        className="flex-shrink-0"
      >
        <Button
          className={clsx(
            'relative aspect-video h-20 flex-shrink-0 rounded-lg border p-3 transition-all sm:h-32',
            isCurrentSlide
              ? 'border-4 border-blue-500 shadow-sm'
              : 'border-gray-200',
          )}
          style={{
            ...slideStyles,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          onClick={() => setCurrentSlide(index)}
        >
          {renderSlideNumber(index)}
          <div className="flex h-full w-full flex-col justify-start rounded">
            <div className="h-full w-full overflow-visible">
              {!isContentSlide
                ? renderTitleSlide(slide)
                : renderContentSlide(slide)}
            </div>
          </div>
        </Button>
      </div>
    );
  };

  return (
    <div className="flex-1">
      <div className="flex h-full gap-2 overflow-x-auto pb-2 sm:gap-3">
        {slides.map(renderSlidePreview)}
      </div>
    </div>
  );
}
